import { z } from 'zod';
import { weeklyBusinessHoursSchema } from './business-hours.js';
import { ianaTimezoneSchema } from './us-iana-timezone-options.js';
export const DEFAULT_AGENT_NAME = 'Maia';
export const MAX_VOICE_AGENT_NAME_LENGTH = 100;
export const voiceAgentNameSchema = z.string().trim().min(1).max(MAX_VOICE_AGENT_NAME_LENGTH);
const VOICE_OPTIONS_RAW = [
    {
        value: '11labs-Grace',
        label: 'Grace',
        attributes: '',
        imageUrl: 'https://retell-utils-public.s3.us-west-2.amazonaws.com/grace.png',
        audioPreviewUrl: 'https://retell-utils-public.s3.us-west-2.amazonaws.com/grace.mp3',
    },
    {
        value: '11labs-Cimo',
        label: 'Cimo',
        attributes: '',
        imageUrl: 'https://retell-utils-public.s3.us-west-2.amazonaws.com/cimo.png',
        audioPreviewUrl: 'https://retell-utils-public.s3.us-west-2.amazonaws.com/cimo.mp3',
    },
    {
        value: '11labs-Susan',
        label: 'Susan',
        attributes: '',
        imageUrl: 'https://retell-utils-public.s3.us-west-2.amazonaws.com/Susan.png',
        audioPreviewUrl: 'https://retell-utils-public.s3.us-west-2.amazonaws.com/susan.mp3',
    },
    {
        value: '11labs-Steve',
        label: 'Steve',
        attributes: '',
        imageUrl: 'https://retell-utils-public.s3.us-west-2.amazonaws.com/Steve.png',
        audioPreviewUrl: 'https://retell-utils-public.s3.us-west-2.amazonaws.com/Steve-.mp3',
    },
    {
        value: '11labs-Myra',
        label: 'Myra',
        attributes: '',
        imageUrl: 'https://retell-utils-public.s3.us-west-2.amazonaws.com/Myra.png',
        audioPreviewUrl: 'https://retell-utils-public.s3.us-west-2.amazonaws.com/myra.mp3',
    },
    {
        value: '11labs-James',
        label: 'James',
        attributes: '',
        imageUrl: 'https://retell-utils-public.s3.us-west-2.amazonaws.com/james.png',
        audioPreviewUrl: 'https://retell-utils-public.s3.us-west-2.amazonaws.com/James.mp3',
    },
];
/** Single source of truth for voice options (IDs, labels, attributes, image URLs). Used by API and UI. */
export const VOICE_OPTIONS = VOICE_OPTIONS_RAW;
export const allowedVoiceIdSchema = z.enum(VOICE_OPTIONS_RAW.map(o => o.value));
export const ALLOWED_VOICE_IDS = allowedVoiceIdSchema.options;
/** Default voice ID when none is set (first option). */
export const DEFAULT_VOICE_ID = VOICE_OPTIONS_RAW[0].value;
/**
 * Prefix marking ElevenLabs voice IDs. Spoken pronunciation_dictionary playback works only on
 * these voices; custom voices (other prefixes) still feed boosted_keywords for transcription.
 */
export const ELEVENLABS_VOICE_ID_PREFIX = '11labs-';
const voiceIdOptionSchema = z.object({
    value: z.string(),
    label: z.string(),
    attributes: z.string(),
    imageUrl: z.string(),
    audioPreviewUrl: z.string().optional(),
});
/** Max custom-pronunciation entries per agent in v1. */
export const MAX_PRONUNCIATION_ENTRIES = 25;
/**
 * A single custom-pronunciation entry. Reach stores the ordered list as the source of truth;
 * Retell receives `pronunciation_dictionary` (audio) + `boosted_keywords` (transcription) downstream.
 * `cmu` stays supported for Retell compatibility but is hidden from tenants (UI is IPA-only).
 */
export const voiceAgentPronunciationEntrySchema = z.object({
    id: z.string(),
    word: z.string().min(1).max(120),
    /** Plain-English hint (e.g. "rhymes with swimmer"); guidance only, not sent to Retell. */
    soundsLike: z.string().min(1).max(200).optional(),
    alphabet: z.enum(['ipa', 'cmu']),
    phoneme: z.string().min(1).max(200),
});
/** PATCH input variant: `id` is optional so new entries can be sent without a server id. */
export const voiceAgentPronunciationEntryInputSchema = voiceAgentPronunciationEntrySchema.extend({
    id: z.string().optional(),
});
/** GET /voice/agent-settings response and merged agent customization */
export const voiceAgentSettingsResponseSchema = z.object({
    agentName: z.string(),
    canOverrideAgentName: z.boolean(),
    voiceId: z.string(),
    greetingIdentifyAsAi: z.boolean(),
    greetingGreetByName: z.boolean(),
    businessFriendlyName: z.string(),
    /** Resolved IANA id (never empty; null DB → default in `@reach/shared-types/us-iana-timezone-options`). */
    timezone: z.string(),
    /** Business-level weekly schedule (stored on the business row); null when not configured. */
    businessHours: weeklyBusinessHoursSchema.nullable(),
    pronunciations: z.array(voiceAgentPronunciationEntrySchema).max(MAX_PRONUNCIATION_ENTRIES),
    voiceIdOptions: z.array(voiceIdOptionSchema).optional(),
});
/** PATCH /voice/agent-settings request body (all fields optional). Empty agentName uses DEFAULT_AGENT_NAME. */
export const voiceAgentSettingsPatchSchema = z.object({
    agentName: z.string().max(MAX_VOICE_AGENT_NAME_LENGTH).optional(),
    voiceId: allowedVoiceIdSchema.optional(),
    greetingIdentifyAsAi: z.boolean().optional(),
    greetingGreetByName: z.boolean().optional(),
    businessFriendlyName: z.string().max(255).nullable().optional(),
    /** PATCH null clears stored override; GET resolves via `@reach/shared-types/us-iana-timezone-options`. */
    timezone: ianaTimezoneSchema.nullable().optional(),
    /** Written to the business row (business-level, shared across products). PATCH null clears it. */
    businessHours: weeklyBusinessHoursSchema.nullable().optional(),
    /** Full ordered list replacement; the service normalizes and assigns ids to new entries. */
    pronunciations: z
        .array(voiceAgentPronunciationEntryInputSchema)
        .max(MAX_PRONUNCIATION_ENTRIES)
        .optional(),
});
/** POST /voice/pronunciation-suggestions request body. */
export const voicePronunciationSuggestionRequestSchema = z.object({
    word: z.string().min(1).max(120),
    soundsLike: z.string().min(1).max(200).optional(),
    businessName: z.string().min(1).max(255).optional(),
});
/** A single AI-generated suggestion. v1 only emits IPA. */
export const voicePronunciationSuggestionSchema = z.object({
    alphabet: z.literal('ipa'),
    phoneme: z.string(),
    confidence: z.number().optional(),
    explanation: z.string().optional(),
});
/** POST /voice/pronunciation-suggestions response; `suggestion` is null when AI is unavailable. */
export const voicePronunciationSuggestionResponseSchema = z.object({
    suggestion: voicePronunciationSuggestionSchema.nullable(),
});
//# sourceMappingURL=voice-agent-settings.js.map